// @ts-nocheck
import { compact } from "../../entities";
import { CFG } from "../../data";
import { clamp, lerp, rand, TAU } from "../../util";
import { Game } from "../game-base";

function hash01(n) {
  const x = Math.sin(n * 127.1 + n * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function wrapScreen(x, y, W, H) {
  return [((x % W) + W) % W, ((y % H) + H) % H];
}

function voidDebrisDepth(d) {
  const [pMin, pMax] = CFG.voidDebrisParallax;
  return (d.parallax - pMin) / (pMax - pMin + 1e-6);
}

const voidRockCache = new Map();

function debrisSize(seed) {
  const [sMin, sMax] = CFG.voidDebrisSize;
  const roll = hash01(seed + 2.1);
  const skew = 0.65 + hash01(seed + 5.2) * 1.1;
  return lerp(sMin, sMax, Math.pow(roll, skew));
}

const VOID_DEBRIS_RESPAWN_S = 0.42;

Game.prototype.createVoidDebrisPiece = function (seed, opts) {
  const fadeIn = opts && opts.fadeIn;
  const [pMin, pMax] = CFG.voidDebrisParallax;
  const [dMin, dMax] = CFG.voidDebrisDrift;
  const parallax = lerp(pMin, pMax, hash01(seed));
  const depth = (parallax - pMin) / (pMax - pMin + 1e-6);
  const drift = lerp(dMin, dMax, depth) * rand(0.55, 1.35);
  const moveA = rand(0, TAU);
  return {
    x: rand(0, this.W),
    y: rand(0, this.H),
    parallax,
    vx: Math.cos(moveA) * drift,
    vy: Math.sin(moveA) * drift,
    size: debrisSize(seed),
    rot: rand(0, TAU),
    spin: rand(-0.35, 0.35) * (0.6 + depth * 0.8),
    phase: rand(0, TAU),
    bob: rand(0.35, 1.1),
    tone: hash01(seed + 3.7),
    verts: 5 + Math.floor(hash01(seed + 9.1) * 3),
    seed,
    fadeIn: fadeIn ? 0 : 1,
  };
};

Game.prototype.bakeVoidRockSprite = function (d) {
  const baseR = 1;
  const n = d.verts;
  const pts = [];
  let minX = 0, maxX = 0, minY = 0, maxY = 0;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU;
    const k = 0.55 + 0.45 * hash01(d.seed + i * 4.17);
    const px = Math.cos(a) * baseR * k;
    const py = Math.sin(a) * baseR * k * (0.62 + hash01(d.seed + i * 2.3) * 0.22);
    pts.push(px, py);
    minX = Math.min(minX, px); maxX = Math.max(maxX, px);
    minY = Math.min(minY, py); maxY = Math.max(maxY, py);
  }
  const pad = 2;
  const w = Math.ceil(maxX - minX + pad * 2);
  const h = Math.ceil(maxY - minY + pad * 2);
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const c = cv.getContext("2d");
  c.translate(w * 0.5, h * 0.5);
  c.beginPath();
  for (let i = 0; i < n; i++) {
    const px = pts[i * 2], py = pts[i * 2 + 1];
    if (i === 0) c.moveTo(px, py);
    else c.lineTo(px, py);
  }
  c.closePath();
  const base = d.tone;
  const top = `hsl(${248 + base * 18}, ${14 + base * 8}%, ${22 + base * 14}%)`;
  const bot = `hsl(${248 + base * 10}, ${10 + base * 6}%, ${12 + base * 8}%)`;
  const g = c.createLinearGradient(-baseR, -baseR * 0.5, baseR * 0.6, baseR * 0.6);
  g.addColorStop(0, top);
  g.addColorStop(1, bot);
  c.fillStyle = g;
  c.fill();
  c.strokeStyle = `rgba(180, 188, 230, ${0.08 + base * 0.1})`;
  c.lineWidth = 0.6;
  c.stroke();
  return { canvas: cv, w, h, baseR };
};

Game.prototype.voidRockSprite = function (d) {
  const key = `vr|${d.verts}|${d.seed}`;
  let spr = voidRockCache.get(key);
  if (!spr) {
    spr = this.bakeVoidRockSprite(d);
    voidRockCache.set(key, spr);
  }
  return spr;
};

Game.prototype.makeVoidAmbient = function () {
  this.voidDebris = [];
  this._voidDebrisTarget = Math.floor((this.W * this.H) / 42000);
  this._voidDebrisSpawnSeq = this._voidDebrisTarget;
  this._voidDebrisRespawnTimer = 0;
  for (let i = 0; i < this._voidDebrisTarget; i++) {
    this.voidDebris.push(this.createVoidDebrisPiece(i * 17.31 + 0.5));
  }
  this.shootingStars = [];
  this._voidShootTimer = rand(CFG.voidShootInterval[0], CFG.voidShootInterval[1]);
};

Game.prototype.updateVoidDebrisRespawn = function (dt) {
  const target = this._voidDebrisTarget || 0;
  if (!this.voidDebris) this.voidDebris = [];
  if (this.voidDebris.length >= target) return;
  this._voidDebrisRespawnTimer -= dt;
  while (this.voidDebris.length < target && this._voidDebrisRespawnTimer <= 0) {
    this._voidDebrisRespawnTimer += VOID_DEBRIS_RESPAWN_S * rand(0.75, 1.25);
    const seed = (this._voidDebrisSpawnSeq++) * 17.31 + rand(0, 1);
    this.voidDebris.push(this.createVoidDebrisPiece(seed, { fadeIn: true }));
  }
};

Game.prototype.voidDebrisScreenPos = function (d) {
  const px = this.camX * CFG.starParallax;
  const py = this.camY * CFG.starParallax;
  const k = d.parallax / CFG.starParallax;
  return wrapScreen(d.x - px * k, d.y - py * k, this.W, this.H);
};

Game.prototype.kickVoidDebrisFromNukeCloud = function (cloud, prevR, rNow) {
  const debris = this.voidDebris;
  if (!debris?.length || rNow <= prevR) return;
  if (!cloud._voidDebrisHit) cloud._voidDebrisHit = new Set();
  const scale = this.scale || 1;
  const scaleY = this.scaleY || scale;
  const scaleAvg = (scale + scaleY) * 0.5;
  const sx = this.cx + (cloud.x - this.camX) * scale;
  const sy = this.cy + (cloud.y - this.camY) * scaleY;
  const n = cloud.reach.length;

  for (const d of debris) {
    if (cloud._voidDebrisHit.has(d)) continue;
    const [dx, dy] = this.voidDebrisScreenPos(d);
    const distPx = Math.hypot(dx - sx, dy - sy);
    const ang = Math.atan2(dy - sy, dx - sx);
    const norm = ((ang % TAU) + TAU) % TAU;
    const sector = Math.min(n - 1, Math.floor(norm / TAU * n));
    const frontNow = this.meteorNukeSectorFront(cloud, sector, rNow) * scaleAvg;
    const frontPrev = this.meteorNukeSectorFront(cloud, sector, prevR) * scaleAvg;
    if (frontPrev >= distPx || frontNow < distPx - 3) continue;

    cloud._voidDebrisHit.add(d);
    const nx = distPx > 0.5 ? (dx - sx) / distPx : Math.cos(ang);
    const ny = distPx > 0.5 ? (dy - sy) / distPx : Math.sin(ang);
    const depth = voidDebrisDepth(d);
    const speed = rand(1100, 1900) * (0.85 + depth * 0.35);
    d.blown = true;
    d.vx = 0;
    d.vy = 0;
    d.kickVx = nx * speed;
    d.kickVy = ny * speed;
    d.spin += rand(-14, 14);
  }
};

Game.prototype.voidDebrisUnwrappedScreen = function (d) {
  const px = this.camX * CFG.starParallax;
  const py = this.camY * CFG.starParallax;
  const k = d.parallax / CFG.starParallax;
  return [d.x - px * k, d.y - py * k];
};

Game.prototype.updateVoidAmbient = function (dt) {
  const W = this.W, H = this.H;
  for (const d of this.voidDebris || []) {
    if (d.blown) {
      d.x += d.kickVx * dt;
      d.y += d.kickVy * dt;
    } else {
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      if (d.x < 0) d.x += W;
      else if (d.x >= W) d.x -= W;
      if (d.y < 0) d.y += H;
      else if (d.y >= H) d.y -= H;
    }
  }
  compact(this.voidDebris, d => {
    if (!d.blown) return true;
    const [sx, sy] = this.voidDebrisUnwrappedScreen(d);
    const pad = 80;
    return sx > -pad && sx < W + pad && sy > -pad && sy < H + pad;
  });
  for (const d of this.voidDebris) {
    if (d.fadeIn != null && d.fadeIn < 1) d.fadeIn = Math.min(1, d.fadeIn + dt * 0.85);
  }
  this.updateVoidDebrisRespawn(dt);

  if (!this.shootingStars) this.shootingStars = [];
  for (const s of this.shootingStars) s.t += dt;
  compact(this.shootingStars, s => s.t < s.life);

  this._voidShootTimer -= dt;
  if (this._voidShootTimer <= 0) {
    this._voidShootTimer = rand(CFG.voidShootInterval[0], CFG.voidShootInterval[1]);
    if (this.shootingStars.length < 2 && Math.random() <= CFG.voidShootChance) {
      this.spawnShootingStar();
    }
  }
};

Game.prototype.spawnShootingStar = function () {
  const W = this.W, H = this.H;
  const edge = Math.floor(Math.random() * 4);
  let x, y, tx, ty;
  const pad = rand(40, 140);
  if (edge === 0) { x = rand(0, W); y = -pad; tx = rand(0, W); ty = H + pad; }
  else if (edge === 1) { x = W + pad; y = rand(0, H); tx = -pad; ty = rand(0, H); }
  else if (edge === 2) { x = rand(0, W); y = H + pad; tx = rand(0, W); ty = -pad; }
  else { x = -pad; y = rand(0, H); tx = W + pad; ty = rand(0, H); }
  const dx = tx - x, dy = ty - y;
  const dist = Math.hypot(dx, dy) || 1;
  const speed = rand(CFG.voidShootSpeed[0], CFG.voidShootSpeed[1]);
  const life = dist / speed;
  this.shootingStars.push({
    x, y, vx: dx / dist * speed, vy: dy / dist * speed,
    t: 0, life,
    trail: rand(110, 240),
    width: rand(0.45, 0.95),
    bright: rand(0.16, 0.38),
  });
};

Game.prototype.drawVoidRock = function (c, x, y, d, t) {
  const spr = this.voidRockSprite(d);
  const rot = d.rot + t * d.spin;
  const bob = Math.sin(t * d.bob + d.phase) * d.size * 0.16;
  const sway = Math.cos(t * d.bob * 0.83 + d.phase * 1.7) * d.size * 0.08;
  const r = d.size * (0.78 + 0.22 * Math.sin(t * d.bob * 0.7 + d.phase));
  const scale = r / spr.baseR;
  c.save();
  c.translate(x + sway, y + bob);
  c.rotate(rot);
  c.drawImage(spr.canvas, -spr.w * scale * 0.5, -spr.h * scale * 0.5, spr.w * scale, spr.h * scale);
  c.restore();
};

Game.prototype.drawVoidDebris = function () {
  const c = this.ctx;
  const t = this.nowS;
  const W = this.W, H = this.H;
  const px = this.camX * CFG.starParallax;
  const py = this.camY * CFG.starParallax;
  for (const d of this.voidDebris || []) {
    let x, y;
    if (d.blown) {
      [x, y] = this.voidDebrisUnwrappedScreen(d);
    } else {
      const driftX = d.x - px * (d.parallax / CFG.starParallax);
      const driftY = d.y - py * (d.parallax / CFG.starParallax);
      [x, y] = wrapScreen(driftX, driftY, W, H);
    }
    const depth = voidDebrisDepth(d);
    c.globalAlpha = (0.22 + depth * 0.38) * (d.fadeIn != null ? d.fadeIn : 1);
    this.drawVoidRock(c, x, y, d, t);
  }
  c.globalAlpha = 1;
};

Game.prototype.drawShootingStars = function () {
  const c = this.ctx;
  const stars = this.shootingStars;
  if (!stars || !stars.length) return;
  const t = this.nowS;
  c.save();
  for (const s of stars) {
    const k = clamp(s.t / s.life, 0, 1);
    const fadeIn = clamp(s.t / 0.22, 0, 1);
    const fadeOut = 1 - k * k * k;
    const a = s.bright * fadeIn * fadeOut;
    if (a < 0.015) continue;
    const hx = s.x + s.vx * s.t;
    const hy = s.y + s.vy * s.t;
    const spd = Math.hypot(s.vx, s.vy) || 1;
    const ux = s.vx / spd, uy = s.vy / spd;
    const tx = hx - ux * s.trail;
    const ty = hy - uy * s.trail;
    const tw = 0.88 + 0.12 * Math.sin(t * 9 + hx * 0.02 + hy * 0.03);

    c.globalCompositeOperation = "lighter";
    c.strokeStyle = `rgba(180, 195, 245, ${a * 0.1})`;
    c.lineWidth = s.width * 3.4;
    c.lineCap = "round";
    c.beginPath();
    c.moveTo(tx, ty);
    c.lineTo(hx, hy);
    c.stroke();

    c.globalCompositeOperation = "source-over";
    const grad = c.createLinearGradient(tx, ty, hx, hy);
    grad.addColorStop(0, "rgba(100, 115, 160, 0)");
    grad.addColorStop(0.35, `rgba(130, 145, 190, ${a * 0.14})`);
    grad.addColorStop(0.72, `rgba(165, 178, 220, ${a * 0.32})`);
    grad.addColorStop(1, `rgba(205, 214, 255, ${a * 0.52 * tw})`);
    c.strokeStyle = grad;
    c.lineWidth = s.width;
    c.beginPath();
    c.moveTo(tx, ty);
    c.lineTo(hx, hy);
    c.stroke();

    const glowR = s.width * 6.2;
    c.globalCompositeOperation = "lighter";
    const halo = c.createRadialGradient(hx, hy, 0, hx, hy, glowR);
    halo.addColorStop(0, `rgba(220, 228, 255, ${a * 0.62 * tw})`);
    halo.addColorStop(0.28, `rgba(190, 205, 255, ${a * 0.22})`);
    halo.addColorStop(0.62, `rgba(160, 175, 230, ${a * 0.07})`);
    halo.addColorStop(1, "rgba(120, 135, 190, 0)");
    c.fillStyle = halo;
    c.beginPath();
    c.arc(hx, hy, glowR, 0, TAU);
    c.fill();

    c.fillStyle = `rgba(205, 214, 255, ${a * 0.85 * tw})`;
    c.beginPath();
    c.arc(hx, hy, s.width * 0.42, 0, TAU);
    c.fill();
    c.fillStyle = `rgba(245, 248, 255, ${a * 0.55 * tw})`;
    c.beginPath();
    c.arc(hx, hy, s.width * 0.18, 0, TAU);
    c.fill();
  }
  c.globalCompositeOperation = "source-over";
  c.globalAlpha = 1;
  c.restore();
};

Game.prototype.drawVoidAmbient = function () {
  this.drawVoidDebris();
  this.drawShootingStars();
};
